import services from '@/services/ascentiaServiceIndex'
import store from '@/stores/index'
import { hasResourcePermission } from '@/utilities/userResourcePermissions'
import { MAINTENANCE_ISSUES_CONSTS as MIC, USER_CONSTS as UC } from '../consts'
import {
  removalsColumnDefs,
  faultMessagesColumnDefs,
  phmAlertColumnDefs,
} from '@/utilities/maintenance'
import moment from 'moment'
import _ from 'lodash'
import _uniq from 'lodash/uniq'
import _flatten from 'lodash/flatten'
import {
  API_DATE_FORMAT,
  COLLINS_PHM_TIER_4,
  RESOURCES,
  PERMISSION_LEVEL_CONSTS,
  TIME_TO_LIVE_MINUTE,
} from '@/utilities/appConsts'

const state = {
  apiErrors: [],
  loadingIssues: false,
  maintenanceIssues: [],
  maintenanceGrid: [],
  componentSummary: null,
  statusSummary: null,
  daysSinceSummary: null,
  maintenanceRecs: [],
  maintenanceStatuses: [],
  investigatorUsers: [],
  detailDiscussionUsers: [],
  detailsDiscussionTypeID: 0,
  investigationDiscussionTypeID: 0,
  supportingInfoSelectionTypes: [],
  airlineResponseOptions: [],
  reasonToCloseOptions: [],
  fixEffectivenessOptions: [],
  analyticDetectionOutcomeOptions: [],
  serviceDeliveryOutcomeOptions: [],
  customAlertMaintenanceIssueVersion: {},
  severityOptions: [],
  mxRec: {
    removalsData: {},
    faultMessages: {},
    phmAlerts: {},
    plots: [],
    visiblePlots: [],
    tail: '',
    system: '',
    component: '',
    recommendation: '',
    partNumber: '',
    estimatedGroundTime: 0,
    ammtask: '',
    internalNotes: '',
    externalNotes: '',
    reviewStatus: 'NEW',
    attachments: [],
    attachmentsCaption: '',
    plotsCaption: '',
    issue: {
      id: 0,
      tail: '',
      airline: '',
      disposition: '',
      initialDate: '',
      dcRisk: '',
      alerts: [],
      count: 0,
    },
    source: {},
    status: {},
    detailsDiscussion: [],
    investigationDiscussion: [],
    statusUpdates: [],
    lastUpdatedBy: '',
    dateSubmitted: null,
    dateCompleted: null,
    serviceAlertNumber: null,
    purchaseOrderNumber: null,
    removedPartNumber: null,
    removedPartSerialNumber: null,
    installedPartNumber: null,
    installedPartSerialNumber: null,
  },
  preservedMxRec: {},
  sessionExpiration: false,
  homePageMaintenanceIssues: [],
  relatedAlerts: [],
  relatedAlertsAsUpdater: [],
  isMxRecAlertsExportLoading: false,
  isDetailsDiscussionLoading: false,
  isInvestigationDiscussionLoading: false,
  issueSourceOptions: [],
  removalsDataByTail: [],
  analyticPagePlots: [],
  analyticPlot6MonthsFromDate: null,
  analyticPlot30DaysFromDate: null,
  analyticPlotToDate: null,
}

const eliminateDoubles = (...arrays) => {
  let jointArray = []
  arrays.forEach(array => {
    jointArray = [...jointArray, ...array]
  })
  return [...new Set([...jointArray])]
}

const getters = {
  allIssues(state) {
    if (state.maintenanceIssues) {
      return state.maintenanceIssues
    }
  },
  isPreSubmittal() {
    // need to iterate through statuses here and match by id
    const stateObj = state.maintenanceStatuses?.find(
      status => status.id === state.mxRec?.status?.id,
    )

    return !stateObj.isExternal || !stateObj.isTerminal
  },
  mappedMaintenanceRecs(state, getters, rootState, rootGetters) {
    if (state.maintenanceRecs) {
      const selections = rootGetters['globalFilter/selections']
      return state.maintenanceRecs
        .filter(
          item =>
            selections.length === 0 ||
            selections.filter(
              selection =>
                selection.airlineId === item.airlineId &&
                selection.masterModelId === item.masterModelId,
            ).length > 0,
        )
        .map(item => {
          return {
            ...item,
            airlineName:
              item.airlineName === null || item.airlineName === ''
                ? 'N/A'
                : item.airlineName,
            masterModelName:
              item.masterModelName === null || item.masterModelName === ''
                ? 'N/A'
                : item.masterModelName,
            lastAlertDateInt: new Date(item.lastAlertDate).getTime(),
            analyticName:
              item.analyticName === null || item.analyticName === ''
                ? 'N/A'
                : item.analyticName,
            analyticModule:
              item.analyticModule === null || item.analyticModule === ''
                ? 'N/A'
                : item.analyticModule,
            systemName: item.systemName === null ? '-' : item.systemName,
            componentName:
              item.componentName === null
                ? '-'
                : item.positionName
                ? `${item.componentName} (${item.positionName})`
                : item.componentName,
          }
        })
    }
    return []
  },

  mappedMaintenanceGrid(state) {
    if (state.maintenanceGrid) {
      return state.maintenanceGrid
    }
    return []
  },

  getSessionStatus(state) {
    return state.sessionExpiration
  },
  manualMxRecSources(state) {
    const manualMxRecSources = state.issueSourceOptions?.filter(
      source => source.isManual,
    )
    return manualMxRecSources
  },
}

const mutations = {
  [MIC.UPDATE_REMOVALS_DATA_BY_TAIL]: (state, data) =>
    (state.removalsDataByTail = data),
  [MIC.UPDATE_MAINTENANCE_ISSUES]: (state, data) =>
    (state.maintenanceIssues = data),
  [MIC.UPDATE_MAINTENANCE_STATUSES]: (state, data) =>
    (state.maintenanceStatuses = data),
  [MIC.UPDATE_COMPONENT_SUMMARY]: (state, data) => {
    state.componentSummary = data
  },
  [MIC.UPDATE_CUSTOM_ALERT_MAINTENANCE_ISSUE_VERSION]: (state, data) => {
    state.customAlertMaintenanceIssueVersion = data
  },
  [MIC.UPDATE_STATUS_SUMMARY]: (state, data) => {
    state.statusSummary = data
  },
  [MIC.UPDATE_TOP_DAYS_SINCE]: (state, data) => {
    state.daysSinceSummary = data
  },
  [MIC.UPDATE_SEVERITY_OPTIONS]: (state, data) => {
    state.severityOptions = data
  },
  [MIC.UPDATE_FIX_EFFECTIVENESS_OPTIONS]: (state, data) => {
    state.fixEffectivenessOptions = data
  },
  [MIC.UPDATE_SERVICE_DELIVERY_OUTCOME_OPTIONS]: (state, data) => {
    state.serviceDeliveryOutcomeOptions = data
  },
  [MIC.UPDATE_ANALYTIC_DETECTION_OUTCOME_OPTIONS]: (state, data) => {
    state.analyticDetectionOutcomeOptions = data
  },
  [MIC.UPDATE_REASON_TO_CLOSE_OPTIONS]: (state, data) => {
    state.reasonToCloseOptions = data
  },
  [MIC.UPDATE_AIRLINE_RESPONSE_OPTIONS]: (state, data) => {
    state.airlineResponseOptions = data
  },
  [MIC.UPDATE_MAINTENANCE_RECS]: (state, data) => {
    state.maintenanceRecs = data
  },
  [MIC.UPDATE_MAINTENANCE_GRID]: (state, data) => {
    state.maintenanceGrid = data
  },
  [MIC.UPDATE_SESSION_EXPIRATION]: (state, data) =>
    (state.sessionExpiration = data),
  [MIC.UPDATE_REMOVALS_DATA]: (state, data) =>
    (state.mxRec.removalsData = { ...state.mxRec.removalsData, ...data }),
  [MIC.UPDATE_FAULT_MESSAGES_DATA]: (state, data) =>
    (state.mxRec.faultMessages = { ...state.mxRec.faultMessages, ...data }),
  [MIC.UPDATE_PHM_ALERTS]: (state, data) =>
    (state.mxRec.phmAlerts = { ...state.mxRec.phmAlerts, ...data }),
  [MIC.UPDATE_ACTIVE_MXREC]: (state, data) => {
    state.mxRec = {
      ...state.mxRec,
      ...data,
    }
  },
  [MIC.UPDATE_ANALYTIC_PAGE_PLOTS]: (state, data) => {
    state.analyticPagePlots = data
  },
  [MIC.UPDATE_ANALYTIC_PLOT_6_MONTHS_FROM_DATE]: (state, data) => {
    state.analyticPlot6MonthsFromDate = data
  },
  [MIC.UPDATE_ANALYTIC_PLOT_30_DAYS_FROM_DATE]: (state, data) => {
    state.analyticPlot30DaysFromDate = data
  },
  [MIC.UPDATE_ANALYTIC_PLOT_TO_DATE]: (state, data) => {
    state.analyticPlotToDate = data
  },
  [MIC.UPDATE_PRESERVED_MXREC]: (state, data) => {
    state.preservedMxRec = JSON.parse(
      JSON.stringify({
        ...data,
      }),
    )
  },
  [MIC.UPDATE_DETAILS_DISCUSSION]: (state, data) => {
    state.mxRec.detailsDiscussion = data === '' ? [] : data
  },
  [MIC.UPDATE_INVESTIGATION_DISCUSSION]: (state, data) => {
    state.mxRec.investigationDiscussion = data === '' ? [] : data
  },
  [MIC.UPDATE_PRESERVED_DETAILS_DISCUSSION]: (state, data) => {
    state.preservedMxRec.detailsDiscussion = JSON.parse(
      JSON.stringify(data === '' ? [] : data),
    )
  },
  [MIC.UPDATE_PRESERVED_INVESTIGATION_DISCUSSION]: (state, data) => {
    state.preservedMxRec.investigationDiscussion = JSON.parse(
      JSON.stringify(data === '' ? [] : data),
    )
  },
  [MIC.UPDATE_PRESERVED_STATUS_UPDATES]: (state, data) => {
    state.preservedMxRec.statusUpdates = JSON.parse(
      JSON.stringify(data === '' ? [] : data),
    )
  },
  [MIC.UPDATE_DETAILS_DISCUSSION_ID]: (state, data) =>
    (state.detailsDiscussionTypeID = data),
  [MIC.UPDATE_INVESTIGATION_DISCUSSION_ID]: (state, data) =>
    (state.investigationDiscussionTypeID = data),
  [MIC.UPDATE_INVESTIGATOR_USERS]: (state, data) =>
    (state.investigatorUsers = data),
  [MIC.UPDATE_DETAIL_USERS]: (state, data) =>
    (state.detailDiscussionUsers = data),
  [MIC.ADD_MAINTENANCE_ATTACHMENT]: (state, attachments) => {
    state.mxRec.attachments = eliminateDoubles(
      state.mxRec.attachments,
      attachments,
    )
  },
  [MIC.SET_MAINTENANCE_ATTACHMENT_CAPTION]: (state, attachmentInfo) => {
    const updatedAttachments = state.mxRec.attachments.map(attachmentObj => {
      if (attachmentObj.id === attachmentInfo.id) {
        return {
          ...attachmentObj,
          caption: attachmentInfo.caption,
        }
      } else {
        return attachmentObj
      }
    })
    state.mxRec.attachments = updatedAttachments
  },
  [MIC.SET_ATTACHMENT_VISIBILITY]: (state, attachmentInfo) => {
    const updatedAttachments = state.mxRec.attachments.map(attachmentObj => {
      if (attachmentObj.id === attachmentInfo.fileId) {
        return {
          ...attachmentObj,
          isExternallyVisible: attachmentInfo.visibility,
        }
      } else {
        return attachmentObj
      }
    })
    state.mxRec.attachments = updatedAttachments
  },
  [MIC.REMOVE_MAINTENANCE_ATTACHMENT]: (state, attachmentId) => {
    state.mxRec.attachments = state.mxRec.attachments.filter(
      attachment => attachment.id !== attachmentId,
    )
  },
  [MIC.REMOVE_PLOT_ATTACHMENT]: (state, plotId) => {
    state.mxRec.plots = state.mxRec.plots.filter(plot => plot.id !== plotId)
  },
  [MIC.ADD_PLOT_ATTACHMENT]: (state, plotToAdd) => {
    state.mxRec.plots.push(plotToAdd)
  },
  [MIC.UPDATE_SUPPORTING_INFO_SELECTION_TYPES]: (state, types) =>
    (state.supportingInfoSelectionTypes = types),
  [MIC.UPDATE_SUPPORTING_INFO_SELECTIONS]: (state, selectionParams) => {
    switch (selectionParams.name) {
      case 'PHM_ALERTS':
        state.mxRec.phmAlerts = {
          ...state.mxRec.phmAlerts,
          selectedRows: selectionParams.selections.selections,
          caption: selectionParams.caption,
        }
        break
      // case 'FAULT_MESSAGES':
      //   state.mxRec.faultMessages = {
      //     ...state.mxRec.faultMessages,
      //     selectedRows: selectionParams.selections.selections,
      //     caption: selectionParams.caption,
      //   }
      //   break
      // case 'REMOVALS':
      //   state.mxRec.removalsData = {
      //     ...state.mxRec.removalsData,
      //     selectedRows: selectionParams.selections.selections,
      //     caption: selectionParams.caption,
      //   }
      //   break
      case 'ATTACHMENTS':
        // the selected plots get saved with the other supporting info types
        // the selected attachments get saved as part of the attachments store
        state.mxRec.visiblePlots =
          selectionParams.selections.selections === ''
            ? []
            : selectionParams.selections.selections
        // eslint-disable-next-line no-case-declarations
        if (
          selectionParams.caption !== '' &&
          selectionParams.caption !== undefined
        ) {
          const captionObject = JSON.parse(selectionParams.caption)
          state.mxRec.attachmentsCaption = captionObject.attachmentsCaption
          state.mxRec.plotsCaption = captionObject.plotsCaption
        }
        break

      default:
    }
  },

  [MIC.UPDATE_SUPPORTING_INFO_ROW_SELECTION]: (state, selectedRowParams) => {
    switch (selectedRowParams.name) {
      case 'PHM_ALERTS':
        state.mxRec.phmAlerts = {
          ...state.mxRec.phmAlerts,
          selectedRows: selectedRowParams.selections,
          caption: selectedRowParams.caption,
        }
        break
      case 'FAULT_MESSAGES':
        state.mxRec.faultMessages = {
          ...state.mxRec.faultMessages,
          selectedRows: selectedRowParams.selections,
        }
        break
      case 'REMOVALS':
        state.mxRec.removalsData = {
          ...state.mxRec.removalsData,
          selectedRows: selectedRowParams.selections,
        }
        break
      case 'ATTACHMENTS':
        break

      default:
    }
  },
  [MIC.UPDATE_ROW_VISIBILITY]: (state, selectedRowParams) => {
    let whichType = ''
    switch (selectedRowParams.type.name) {
      case 'PHM_ALERTS':
        whichType = 'phmAlerts'
        // eslint-disable-next-line no-case-declarations
        break
      case 'FAULT_MESSAGES':
        whichType = 'faultMessages'
        break
      case 'REMOVALS':
        whichType = 'removals'
        // will add removals in later
        break
      case 'ATTACHMENTS':
        whichType = 'attachments'
        break
      default:
    }
    // only update the state if it's not there already
    // this will avoid duplicates caused by duplicate events fired
    // when programatically setting the rows (agGrid bug)
    const checkSelectionsForDupes = state.mxRec[whichType].selectedRows.filter(
      id => id === selectedRowParams.row.data.rowId,
    )
    if (selectedRowParams.row.selected) {
      if (checkSelectionsForDupes.length === 0) {
        const updatedSelectedRows = [...state.mxRec[whichType].selectedRows]

        updatedSelectedRows.push(selectedRowParams.row.data.rowId)

        state.mxRec[whichType] = {
          ...state.mxRec[whichType],
          selectedRows: updatedSelectedRows,
        }
      }
    } else {
      let filteredSelectedRows = [...state.mxRec[whichType].selectedRows]
      filteredSelectedRows = filteredSelectedRows.filter(
        row => row !== selectedRowParams.row.data.rowId,
      )
      state.mxRec[whichType] = {
        ...state.mxRec[whichType],
        selectedRows: filteredSelectedRows,
      }
    }
  },
  [MIC.SET_CAPTION]: (state, captionParams) => {
    switch (captionParams.type.name) {
      case 'PHM_ALERTS':
        state.mxRec.phmAlerts = {
          ...state.mxRec.phmAlerts,
          caption: captionParams.caption,
        }
        break
      case 'FAULT_MESSAGES':
        state.mxRec.faultMessages = {
          ...state.mxRec.faultMessages,
          caption: captionParams.caption,
        }
        break
      case 'REMOVALS':
        state.mxRec.removals = {
          ...state.mxRec.removals,
          caption: captionParams.caption,
        }
        break
      case 'ATTACHMENTS':
        state.mxRec.attachmentsCaption = captionParams.attachmentsCaption
        state.mxRec.plotsCaption = captionParams.plotsCaption
        break
    }
  },

  [MIC.SET_MAINTENANCE_ISSUE_PLOTS]: (state, data) => {
    state.mxRec.plots = data
  },

  [MIC.SET_PLOT_VISIBILITY]: (state, data) => {
    let updatedVisiblePlots = [...state.mxRec.visiblePlots]
    if (data.selected) {
      updatedVisiblePlots.push(data.plot)
    } else {
      updatedVisiblePlots = updatedVisiblePlots.filter(visiblePlotId => {
        return data.plot !== visiblePlotId
      })
    }
    state.mxRec.visiblePlots = updatedVisiblePlots
  },

  [MIC.UPDATE_MX_ERRORS]: (state, data) =>
    (state.apiErrors = data ? eliminateDoubles(state.apiErrors, [data]) : []),

  [MIC.UPDATE_HOME_PAGE_MAINTENANCE_ISSUES]: (state, data) => {
    const mappedData = data.map(itemObj => {
      return {
        ...itemObj,
      }
    })
    state.homePageMaintenanceIssues = mappedData
  },

  [MIC.UPDATE_RELATED_ALERTS]: (state, data) => {
    state.relatedAlerts = data
  },
  [MIC.UPDATE_RELATED_ISSUES]: (state, data) => {
    state.relatedAlerts = data
  },

  [MIC.UPDATE_RELATED_ALERTS_AS_UPDATER]: (state, data) => {
    state.relatedAlertsAsUpdater = data
  },

  [MIC.UPDATE_MXREC_ALERTS_EXPORT_LOADING]: (state, data) =>
    (state.isMxRecAlertsExportLoading = data),

  [MIC.UPDATE_MXREC_DISCUSSIONS_LOADING]: (state, data) => {
    if (data.discussionTypeId) {
      if (data.discussionTypeId === state.detailsDiscussionTypeID) {
        state.isDetailsDiscussionLoading = data.loading
      } else if (
        data.discussionTypeId === state.investigationDiscussionTypeID
      ) {
        state.isInvestigationDiscussionLoading = data.loading
      }
    } else {
      state.isDetailsDiscussionLoading = data.loading
      state.isInvestigationDiscussionLoading = data.loading
    }
  },
  [MIC.UPDATE_MXREC_STATUS_UPDATES_LOADING]: (state, data) =>
    (state.isStatusUpdatesLoading = data),
  [MIC.UPDATE_ISSUE_SOURCE_OPTIONS]: (state, data) =>
    (state.issueSourceOptions = data),
  [MIC.UPDATE_STATUS_UPDATES]: (state, data) => {
    state.mxRec = {
      ...state.mxRec,
      statusUpdates: data === '' ? [] : data,
    }
  },
}

const actions = {
  async [MIC.GET_MAINTENANCE_ISSUES]({ commit }) {
    commit(MIC.UPDATE_MAINTENANCE_ISSUES, [])
    const response = await services.maintenanceIssues.getData(
      'maintenanceIssues',
    ).data

    if (response) {
      const data = response.map(item => {
        return {
          ...item,
          airlineId: item.airline.id,
          airlineName: item.airline.name,
          masterModelId: item.masterModel.id,
          masterModelName: item.masterModel.name,
          systemId: item.system.id,
          systemName: item.system.name,
          componentId: item.component.id,
          componentName: item.component.name,
          statusId: item.status.id,
          statusName: item.status.name,
          externalStatus: item.status.isExternal,
          analyticId: item.analytic ? item.analytic.id : '',
          analyticName: item.analytic ? item.analytic.name : '',
          analyticAlert: item.analyticAlert,
          tailId: item.tail.id,
          tailNumber: item.tail.name,
          alertCount: item.alertCount,
          id: item.id,
          issueId: item.issueId || item.id,
          partNumber: item.partNumber,
          ammtask: item.ammtask,
          risk: item.risk,
          alertType: item.source.name,
          createdDate: item.createdDate,
          lastAlertDate: item.lastAlertDate,
          diffDays: item.dateSubmitted
            ? moment().diff(moment.utc(item.dateSubmitted), 'd')
            : null,
        }
      })
      commit(MIC.UPDATE_MAINTENANCE_ISSUES, data)
    }
  },
  async [MIC.GET_MAINTENANCE_STATUSES]({ commit }) {
    try {
      const mxStatuses = (
        await services.maintenanceIssues.getData(
          'maintenanceIssuesStatuses',
          null,
          false,
          TIME_TO_LIVE_MINUTE * 60 * 3,
        )
      ).data
      commit(MIC.UPDATE_MAINTENANCE_STATUSES, mxStatuses)
    } catch (error) {
      console.log(`${[MIC.GET_MAINTENANCE_STATUSES]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'status')
    }
  },
  async [MIC.GET_MAINTENANCE_ISSUES_RELATED_ISSUES](
    { commit },
    ataChapterAndTail,
  ) {
    // TODO: include issue sources here for chronic repeaters
    try {
      const response = await services.maintenanceIssues.getData(
        'maintenanceIssuesRelatedIssues',
        ataChapterAndTail,
        true,
      )

      if (response) {
        const data = response.data.map(item => {
          return {
            issueId: item.id,
            aircraftId: item.tail.id,
            statusName: item.status?.name ? item.status.name : 'N/A',
            ataChapterName: item.ataChapter
              ? item.name
                ? `${item.ataChapter}`
                : item.ataChapter
              : 'N/A',
            analyticModuleName: item.analyticModuleName
              ? item.analyticModuleName
              : 'N/A',
            alertCount: item.alertCount,
            lastAlertDate: item.updatedDate,
            airlineAgreementTypeId: item.airlineAgreementTypeId,
            isExternal: item.status.isExternal,
            chronicRepeaterUniqueId: item.chronicRepeaterUniqueId,
          }
        })
        commit(MIC.UPDATE_RELATED_ISSUES, data)
      }
    } catch (error) {
      console.log(
        `${[MIC.GET_MAINTENANCE_ISSUES_RELATED_ISSUES]}: error ${error}`,
      )
      commit(MIC.UPDATE_MX_ERRORS, 'related issues')
    }
  },
  async [MIC.RESET_ACTIVE_MXREC]({ commit }) {
    const mxRec = {
      removalsData: {},
      faultMessages: {},
      phmAlerts: {},
      tail: '',
      system: '',
      component: '',
      recommendation: '',
      partNumber: '',
      estimatedGroundTime: 0,
      ammtask: '',
      internalNotes: '',
      externalNotes: '',
      reviewStatus: 'NEW',
      attachments: [],
      attachmentsCaption: '',
      issue: {
        id: 0,
        tail: '',
        airline: '',
        disposition: '',
        initialDate: '',
        dcRisk: '',
        alerts: [],
        count: 0,
      },
      source: {},
      status: {},
      detailsDiscussion: [],
      investigationDiscussion: [],
      lastUpdatedBy: '',
      dateSubmitted: null,
      dateCompleted: null,
      serviceAlertNumber: null,
      purchaseOrderNumber: null,
      removedPartNumber: null,
      removedPartSerialNumber: null,
      installedPartNumber: null,
      installedPartSerialNumber: null,
      title: '',
    }
    commit(MIC.UPDATE_ACTIVE_MXREC, mxRec)
    commit(MIC.UPDATE_RELATED_ALERTS_AS_UPDATER, [])
    commit(MIC.UPDATE_RELATED_ALERTS, [])
  },

  async [MIC.GET_SINGLE_MAINTENANCE_REC](
    { commit, state, rootGetters },
    params,
  ) {
    try {
      commit(MIC.UPDATE_ANALYTIC_PAGE_PLOTS, [])
      commit(MIC.UPDATE_ANALYTIC_PLOT_6_MONTHS_FROM_DATE, null)
      commit(MIC.UPDATE_ANALYTIC_PLOT_30_DAYS_FROM_DATE, null)
      commit(MIC.UPDATE_ANALYTIC_PLOT_TO_DATE, null)

      const amcFeatures = rootGetters['analyticModule/mappedAmcFeatures']
      const isFeatureInvestigator = hasResourcePermission(
        {
          resource: RESOURCES.FEATURE_INVESTIGATION,
          permissionLevel: PERMISSION_LEVEL_CONSTS.READ,
        },
        store.state[UC.NAMESPACE].permissions,
      )

      const mxRec = (
        await services.maintenanceIssues.getData(
          'maintenanceIssueById',
          {
            issueId: params.issueId,
          },
          true,
        )
      ).data

      // Dont remove below coomented line of code as its required for future position checking
      // so that we have only labels related to thet poistion
      // const mxRecPositionId = mxRec.positionId || null

      // BEGIN LOGIC FOR CREATING PREPOPULATED ANALYTIC PLOTS
      const tailId = mxRec.tail.id
      const tailName = mxRec.tail.name
      const alerts = mxRec.alerts

      // BEGIN LOGIC FOR CREATING PREPOPULATED ANALYTIC PLOTS BASED ON ALERT DATES
      const groupedAlertDates = _.groupBy(alerts, 'date')
      const alertDateKeys = Object.keys(groupedAlertDates).map(item => item)
      const sortedAlertDateKeys = alertDateKeys.sort()
      let earliestAlertDate = null
      if (sortedAlertDateKeys.length > 0) {
        earliestAlertDate = sortedAlertDateKeys[0] + 'Z'
      }
      const analyticTrendsEarliestDate = earliestAlertDate
        ? moment
            .utc(earliestAlertDate)
            .add(-6, 'months')
            .format(API_DATE_FORMAT)
        : null
      const analyticComparisonEarliestDate = earliestAlertDate
        ? moment.utc(earliestAlertDate).add(-30, 'days').format(API_DATE_FORMAT)
        : null
      let toDate = moment.utc().format(API_DATE_FORMAT)
      // if terminal date is NOT null, then the to date becomes that
      if (mxRec.terminalDate !== null) {
        // check for min date, what do we use?
        const terminalDateObj = new Date(mxRec.terminalDate)
        if (terminalDateObj.getFullYear() > 1) {
          toDate = moment.utc(mxRec.terminalDate).format(API_DATE_FORMAT)
        }
      }

      // BEGIN LOGIC FOR CREATING PREPOPULATED ANALYTIC PLOTS BASED ON ALERT TYPES
      const groupedAlertTypes = _.groupBy(alerts, 'alertTypeId')
      const alertTypes = Object.keys(groupedAlertTypes).map((item, index) => {
        const items = Object.values(groupedAlertTypes)[index]
        return {
          alertTypeId: +item,
          analyticId: items.length > 0 ? items[0].analyticId : 0,
          tailId,
          tailName,
          featureId: items.length > 0 ? items[0].featureId : null,
          riskScoreIds: items.length > 0 ? _uniq(items[0].riskScoreIds) : [],
        }
      })

      // need to build array of feature ID with risk score/public/private info
      let featureIds = alertTypes.map(alertType => {
        return [alertType.featureId, ...alertType.riskScoreIds]
      })

      // this will look like
      // [featureId, [riskscoreIds], featureId, [riskScoreId]] and
      // risk score Ids may be duplicated so need to get unique values
      // use spread and Set to flatten
      featureIds = [...new Set([].concat(...featureIds))]

      const mappedFeatures = _flatten(
        featureIds.map(featureId => {
          const filteredFeaturesByFeatureId = amcFeatures.filter(
            feature =>
              feature.actualId === featureId &&
              feature.analyticId === alertTypes[0].analyticId,
          )
          if (
            filteredFeaturesByFeatureId &&
            filteredFeaturesByFeatureId.length
          ) {
            return filteredFeaturesByFeatureId
          }
        }),
      ).filter(feature => feature !== undefined)

      const alertTypeFeatureIds = mappedFeatures.filter(
        featureObj => featureObj && !featureObj?.isRiskScore,
      )

      const publicFeatureIds = alertTypeFeatureIds.filter(
        featureObj => !featureObj?.isPrivate,
      )

      const privateFeatureIds = alertTypeFeatureIds
        .filter(item => item.isPrivate)
        .map(item => item.featureId)

      // Riskscore-------------------------------------------
      const alertTypeRiskScoreIds = mappedFeatures.filter(
        featureObj => featureObj?.isRiskScore,
      )

      const publicRiskScoreIds = mappedFeatures.filter(
        featureObj => featureObj?.isRiskScore && !featureObj?.isPrivate,
      )

      const privateRiskScoreIds = alertTypeRiskScoreIds
        .filter(item => item.isPrivate)
        .map(item => item.featureId)

      const alertTypesToPlot = alertTypes.map(alertTypeObj => {
        // Feature will be same for the entire alertType object so pick any from the
        // mapped feature (contains undefined for private in updater) or pick by Id
        const foundMappedFeature = mappedFeatures.find(mappedFeature =>
          mappedFeature
            ? alertTypeObj.riskScoreIds.length
              ? alertTypeObj.riskScoreIds.includes(mappedFeature.actualId)
              : alertTypeObj.analyticId
            : false,
        )

        return {
          ...alertTypeObj,
          analyticModuleId: foundMappedFeature
            ? foundMappedFeature.analyticModuleId
            : '',
          featureIds: alertTypeFeatureIds,
          riskScoreIds: alertTypeRiskScoreIds,
          publicFeatureIds: publicFeatureIds,
          privateFeatureIds: privateFeatureIds,
          publicRiskScoreIds: publicRiskScoreIds,
          privateRiskScoreIds: privateRiskScoreIds,
        }
      })

      const plots = []
      if (alertTypesToPlot.length > 0 && mappedFeatures.length > 0) {
        // 9151 = analytic trends
        // 9124 = analytic comparison
        // we need TWO plots of each type(riskscore and analytics)...one with feature Ids and one with risk score Ids
        const plotTypeIds = [9151, 9124, 9151, 9124]

        plotTypeIds.forEach((plotTypeId, index) => {
          const isRiskScoreIndex = index === 0 || index === 1 // index 0 and 1 are both riskscore plots

          const idsToPlot = isRiskScoreIndex
            ? alertTypesToPlot[0].riskScoreIds.map(item => item.riskScoreId)
            : alertTypesToPlot[0].featureIds.map(item => item.featureId)

          // create each a pre-populated for each plotTypeIds
          const plot = {
            order: index,
            plotTypeId,
            analyticModuleId: alertTypesToPlot[0].analyticModuleId,
            featureIds: idsToPlot,
            tailId: alertTypesToPlot[0].tailId,
            isRiskScore: isRiskScoreIndex,
            caption: '',
            isFeatureInvestigator,
            publicFeatureIds: alertTypesToPlot[0].publicFeatureIds,
            publicRiskScoreIds: alertTypesToPlot[0].publicRiskScoreIds,
            privateRiskScoreIds: alertTypesToPlot[0].privateRiskScoreIds,
            privateFeatureIds: alertTypesToPlot[0].privateFeatureIds,
          }

          // rules for determining if the prepopulated plot should be added to the mx rec page
          const isValidPlot = plot => {
            // any plot is valid if the user is a Feature Investigator
            if (plot.isFeatureInvestigator) {
              return true
            }

            // risk score plots are valid if not a Feature Investigator and public risk score ids
            if (plot.isRiskScore && plot.publicRiskScoreIds.length > 0) {
              return true
            }

            // feature plots are vvalid if not a Feature Investigator and public feature ids
            if (!plot.isRiskScore && plot.publicFeatureIds.length > 0) {
              return true
            }

            // all other plots are invalid and should not be available (regardless of visibility)
            return false
          }

          // only add the plot to the mx rec page if valid and
          //check if the plot is isRiskScoreIndex and has atleast one public or private riskscore
          if (
            isValidPlot(plot) &&
            (isRiskScoreIndex
              ? plot.publicRiskScoreIds.length ||
                plot.privateRiskScoreIds.length
              : true)
          ) {
            plots.push(plot)
          }
        })
      }

      // BEGIN COMMIT LOGIC FOR PREPOPULATED ANALYTIC PLOTS
      commit(MIC.UPDATE_ANALYTIC_PAGE_PLOTS, plots)
      commit(
        MIC.UPDATE_ANALYTIC_PLOT_6_MONTHS_FROM_DATE,
        analyticTrendsEarliestDate,
      )
      commit(
        MIC.UPDATE_ANALYTIC_PLOT_30_DAYS_FROM_DATE,
        analyticComparisonEarliestDate,
      )
      commit(MIC.UPDATE_ANALYTIC_PLOT_TO_DATE, toDate)
      // END LOGIC FOR CREATING PREPOPULATED ANALYTIC PLOTS

      commit(MIC.UPDATE_ACTIVE_MXREC, mxRec)
      commit(
        MIC.UPDATE_PRESERVED_MXREC,
        params.preservedMxRecUpdate
          ? {
              ...state.mxRec,
              ...mxRec,
            }
          : mxRec,
      )
    } catch (error) {
      console.log(`${[MIC.GET_SINGLE_MAINTENANCE_REC]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'maintenance')
    }
  },

  async [MIC.UPDATE_SINGLE_MAINTENANCE_ISSUE]({ dispatch, state }, mxRecInfo) {
    // format attachment Ids to work with attachmentIdCaption so that will work
    // when we add ability to save individual captions with attachments
    const attachmentIdCaptions = mxRecInfo.attachments.map(attachmentObj => {
      return {
        attachmentId: attachmentObj.id,
        attachmentCaption: !attachmentObj.caption
          ? null
          : attachmentObj.caption,
      }
    })

    const mxRecToSave = {
      id: mxRecInfo.id,
      airlineAgreementTypeId: mxRecInfo.airlineAgreementTypeId,
      analyticModuleId: mxRecInfo.analyticModuleId,
      recommendation: mxRecInfo.recommendation,
      partNumber: mxRecInfo.partNumber,
      remainingUsefulLife: mxRecInfo.remainingUsefulLife,
      estimatedGroundTime: mxRecInfo.estimatedGroundTime,
      successRate: mxRecInfo.successRate,
      ammtask: mxRecInfo.ammtask,
      maintenanceIssueStatusId: mxRecInfo.maintenanceIssueStatusId,
      internalNotes: mxRecInfo.internalNotes,
      externalNotes: mxRecInfo.externalNotes,
      component: mxRecInfo.component?.id,
      tail: state.mxRec.tail.name,
      risk: mxRecInfo.risk,
      ownerUserId: mxRecInfo.ownerUserId,
      title: mxRecInfo.title,
      // for future use with captions for individual attachments
      attachmentIdCaptions: attachmentIdCaptions,
      dateCompleted: mxRecInfo.dateCompleted,
      serviceAlertNumber: mxRecInfo.serviceAlertNumber,
      purchaseOrderNumber: mxRecInfo.purchaseOrderNumber,
      removedPartNumber: mxRecInfo.removedPartNumber,
      removedPartSerialNumber: mxRecInfo.removedPartSerialNumber,
      installedPartNumber: mxRecInfo.installedPartNumber,
      installedPartSerialNumber: mxRecInfo.installedPartSerialNumber,
      airlineResponseId: mxRecInfo.airlineResponseId,
      closingReasonId: mxRecInfo.closingReasonId,
      fixEffectivenessId: mxRecInfo.fixEffectivenessId,
      analyticDetectionOutcomeId: mxRecInfo.analyticDetectionOutcomeId,
      serviceDeliveryOutcomeId: mxRecInfo.serviceDeliveryOutcomeId,
      severityId: mxRecInfo.severityId,
      expectedServiceInterruptionMinutesSaved:
        mxRecInfo.expectedServiceInterruptionMinutesSaved,
      mroLocation: mxRecInfo.mroLocation,
      mroPoc: mxRecInfo.mroPoc,
      dateOfRemoval: mxRecInfo.dateOfRemoval,
      mxRecSubmitterId: mxRecInfo.mxRecSubmitterId,
      assetSupportPOC: mxRecInfo.assetSupportPOC,
      aircraftId: mxRecInfo.aircraftId,
      sourceId: mxRecInfo.source.id,
      dateSubmitted: mxRecInfo.dateSubmitted,
      positionId: mxRecInfo.positionId,
    }
    state.preservedMxRec = JSON.parse(JSON.stringify(mxRecToSave))
    const mxRec = await services.maintenanceIssues.saveData(
      'maintenanceIssue',
      mxRecToSave,
    )
    if (mxRec.status === 200 && mxRec.data === 1) {
      await dispatch(MIC.GET_SINGLE_MAINTENANCE_REC, {
        issueId: mxRecToSave.id,
        preservedMxRecUpdate: true,
      })
      await dispatch(MIC.GET_MAINTENANCE_GRID, true)
    }
  },

  async [MIC.UPDATE_MAINTENANCE_ATTACHMENTS]({ commit }, attachmentToUpdate) {
    // Attachment calls are taken care of in attachment store
    if (attachmentToUpdate.remove) {
      commit(MIC.REMOVE_MAINTENANCE_ATTACHMENT, attachmentToUpdate.id)
    } else {
      commit(MIC.ADD_MAINTENANCE_ATTACHMENT, attachmentToUpdate.attachments)
      // commit(MIC.UPDATE_PRESERVED_MXREC, state.mxRec)
    }
  },

  [MIC.UPDATE_MAINTENANCE_ATTACHMENT_CAPTION]({ commit }, attachmentToUpdate) {
    commit(MIC.SET_MAINTENANCE_ATTACHMENT_CAPTION, attachmentToUpdate)
  },

  async [MIC.UPDATE_PLOT_ATTACHMENT]({ commit }, plotToUpdate) {
    // Attachment calls are taken care of in attachment store
    if (plotToUpdate.remove) {
      commit(MIC.REMOVE_PLOT_ATTACHMENT, plotToUpdate.plot.id)
    } else {
      commit(MIC.ADD_PLOT_ATTACHMENT, plotToUpdate.plot)
    }
  },

  async [MIC.GET_COMPONENT_SUMMARY]({ commit, rootGetters }) {
    const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
    const masterModelIds = rootGetters['globalFilter/masterModelIdsSelected']
    const filters = {
      airlines: airlineIds,
      platforms: masterModelIds,
    }
    const statuses = (
      await services.maintenanceIssues.getData(
        'componentSummary',
        filters,
        true,
      )
    ).data
    commit(MIC.UPDATE_COMPONENT_SUMMARY, statuses)
  },

  async [MIC.GET_STATUS_SUMMARY]({ commit, rootGetters }) {
    const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
    const masterModelIds = rootGetters['globalFilter/masterModelIdsSelected']
    const filters = {
      airlines: airlineIds,
      platforms: masterModelIds,
    }
    const statuses = (
      await services.maintenanceIssues.getData('statusSummary', filters, true)
    ).data
    commit(MIC.UPDATE_STATUS_SUMMARY, statuses)
  },

  async [MIC.GET_TOP_DAYS_SINCE]({ commit, rootGetters }) {
    const airlines = rootGetters[`${UC.NAMESPACE}/mappedUserAirlines`]
    const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
    const masterModelIds = rootGetters['globalFilter/masterModelIdsSelected']
    const airlineICAO = airlines
      .filter(item => airlineIds.includes(item.airlineId))
      .map(item => item.icaoCode)
    const filters = {
      airlines: airlineICAO,
      platforms: masterModelIds,
    }
    const response = await services.maintenanceIssues.getData(
      'daysSinceSummary',
      filters,
      true,
    )

    let data = []

    if (response) {
      data = response.data.map(item => {
        return {
          ...item,
          statusId: item.status.id,
          id: item.id,
          issueId: item.issueId || item.id,
          lastAlertDate: item.lastAlertDate,
          diffDays: item.dateSubmitted
            ? moment().diff(moment.utc(item.dateSubmitted), 'd')
            : null,
        }
      })
    }
    commit(MIC.UPDATE_TOP_DAYS_SINCE, data)
  },

  async [MIC.GET_STATUS_UPDATE]({ commit }, issueId) {
    try {
      commit(MIC.UPDATE_MXREC_STATUS_UPDATES_LOADING, true)
      const response = await services.statusUpdates.getData(
        'getStatusUpdate',
        issueId,
        true,
      )
      const statusData = response.data
      commit(MIC.UPDATE_STATUS_UPDATES, statusData)
      commit(MIC.UPDATE_PRESERVED_STATUS_UPDATES, statusData)
    } catch (error) {
      console.log(`${[MIC.GET_STATUS_UPDATE]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'status updates')
    } finally {
      commit(MIC.UPDATE_MXREC_STATUS_UPDATES_LOADING, false)
    }
  },

  // make more generic....map over discussion types and only pull the ones you need
  async [MIC.GET_DISCUSSIONS](
    { commit },
    { issueId, discussionTypeId = null },
  ) {
    try {
      commit(MIC.UPDATE_MXREC_DISCUSSIONS_LOADING, {
        discussionTypeId: discussionTypeId || undefined,
        loading: true,
      })
      const allDiscussionTypes = (
        await services.discussion.getData('discussionTypes')
      ).data

      const discussionTypes =
        discussionTypeId && allDiscussionTypes.length
          ? allDiscussionTypes.filter(discussionTypeObj => {
              return discussionTypeObj.id === discussionTypeId
            })
          : allDiscussionTypes

      const maintenanceDiscussionTypes = discussionTypes
        .filter(discussionTypeObj => {
          return (
            discussionTypeObj.name === 'MxRecDetails' ||
            discussionTypeObj.name === 'MxRecInvestigation'
          )
        })
        .map(discussionTypeObj => {
          if (discussionTypeObj.name === 'MxRecDetails') {
            commit(MIC.UPDATE_DETAILS_DISCUSSION_ID, discussionTypeObj.id)
          } else if (discussionTypeObj.name === 'MxRecInvestigation') {
            commit(MIC.UPDATE_INVESTIGATION_DISCUSSION_ID, discussionTypeObj.id)
          }
          return discussionTypeObj.id
        })

      await services.discussion
        .getData(
          'getMxRecDiscussions',
          {
            issueId: issueId,
            discussionTypes: maintenanceDiscussionTypes,
          },
          true,
        )
        .then(res => {
          // eslint-disable-next-line
          res.map(response => {
            const discussion = response.discussionData
            // look at the type ID of the first element in the discusssion
            // to match it back up to what type it is and commit that type
            // to the store
            if (response.discussionTypeId === state.detailsDiscussionTypeID) {
              commit(MIC.UPDATE_DETAILS_DISCUSSION, discussion)
              commit(MIC.UPDATE_PRESERVED_DETAILS_DISCUSSION, discussion)
            } else if (
              response.discussionTypeId === state.investigationDiscussionTypeID
            ) {
              commit(MIC.UPDATE_INVESTIGATION_DISCUSSION, discussion)
              commit(MIC.UPDATE_PRESERVED_INVESTIGATION_DISCUSSION, discussion)
            } else {
              commit(MIC.UPDATE_MX_ERRORS, response)
            }
          })
        })
    } catch (error) {
      console.log(`${[MIC.GET_DISCUSSIONS]}: error ${error}`)
      commit(
        MIC.UPDATE_MX_ERRORS,
        'investigation discussion or maintenance discussion',
      )
    } finally {
      commit(MIC.UPDATE_MXREC_DISCUSSIONS_LOADING, {
        discussionTypeId: discussionTypeId || undefined,
        loading: false,
      })
    }
  },

  [MIC.ADD_DISCUSSION_COMMENT]({ commit }, commentInfo) {
    if (commentInfo.discussionType === state.detailsDiscussionTypeID) {
      const updatedDetailsDiscussion = [
        commentInfo.comment,
        ...state.mxRec.detailsDiscussion,
      ]
      commit(MIC.UPDATE_DETAILS_DISCUSSION, updatedDetailsDiscussion)
    } else if (
      commentInfo.discussionType === state.investigationDiscussionTypeID
    ) {
      const updatedInvestigationDiscussion = [
        commentInfo.comment,
        ...state.mxRec.investigationDiscussion,
      ]
      commit(
        MIC.UPDATE_INVESTIGATION_DISCUSSION,
        updatedInvestigationDiscussion,
      )
    }
  },

  [MIC.REMOVE_DISCUSSION_COMMENT]({ commit }, commentInfo) {
    if (commentInfo.discussionType === state.detailsDiscussionTypeID) {
      const updatedDetailedDiscussion = state.mxRec.detailsDiscussion.map(
        comment => {
          if (comment.id === commentInfo.whichComment) {
            comment.isNew = undefined
            comment.isUpdated = undefined
            comment.isDeleted = true
          }
          return comment
        },
      )
      commit(MIC.UPDATE_DETAILS_DISCUSSION, updatedDetailedDiscussion)
    } else if (
      commentInfo.discussionType === state.investigationDiscussionTypeID
    ) {
      const updatedInvestigationDiscussion =
        state.mxRec.investigationDiscussion.map(comment => {
          if (comment.id === commentInfo.whichComment) {
            comment.isNew = undefined
            comment.isUpdated = undefined
            comment.isDeleted = true
          }
          return comment
        })
      commit(
        MIC.UPDATE_INVESTIGATION_DISCUSSION,
        updatedInvestigationDiscussion,
      )
    }
  },

  [MIC.UPDATE_DISCUSSION_COMMENT]({ commit }, commentInfo) {
    if (commentInfo.discussionType === state.detailsDiscussionTypeID) {
      const updatedDetailsDiscussion = state.mxRec.detailsDiscussion.map(
        commentObj => {
          if (commentObj.id === commentInfo.comment.id) {
            const updatedCommentObj = { ...commentInfo.comment }
            if (commentInfo.comment.newDbId) {
              // if the updated comment obj has the id from the database
              // replace it with that so when the comment gets updated
              // again it will send that id
              updatedCommentObj.id = commentInfo.comment.newDbId
              // use the spread operator to take the newDbId property out since
              // we don't need it anymore
              // eslint-disable-next-line no-unused-vars
              const { newDbId, ...commentObjMinusDbId } = updatedCommentObj
              return commentObjMinusDbId
            } else {
              return updatedCommentObj
            }
          }
          return commentObj
        },
      )
      commit(MIC.UPDATE_DETAILS_DISCUSSION, updatedDetailsDiscussion)
    } else if (
      commentInfo.discussionType === state.investigationDiscussionTypeID
    ) {
      const updatedInvestigationDiscussion =
        state.mxRec.investigationDiscussion.map(commentObj => {
          if (commentObj.id === commentInfo.comment.id) {
            const updatedCommentObj = { ...commentInfo.comment }
            if (commentInfo.comment.newDbId) {
              // if the updated comment obj has the id from the database
              // replace it with that so when the comment gets updated
              // again it will send that id
              updatedCommentObj.id = commentInfo.comment.newDbId
              // use the spread operator to take the newDbId property out since
              // we don't need it anymore
              // eslint-disable-next-line no-unused-vars
              const { newDbId, ...commentObjMinusDbId } = updatedCommentObj
              return commentObjMinusDbId
            } else {
              return updatedCommentObj
            }
          }
          return commentObj
        })
      commit(
        MIC.UPDATE_INVESTIGATION_DISCUSSION,
        updatedInvestigationDiscussion,
      )
    }
  },

  async [MIC.POST_DETAIL_DISCUSSION]({ dispatch, commit }, params) {
    if (params.comment.isNew) {
      commit(MIC.UPDATE_MXREC_DISCUSSIONS_LOADING, {
        discussionTypeId: params.discussionType,
        loading: true,
      })
      await services.discussion
        .saveData('updateComment', {
          comment: {
            issueId: params.issueId,
            link: params.link,
            discussionTypeId: params.discussionType,
            comment: params.comment.comment,
            taggedUsers: params.comment.usersTagged || [],
          },
          action: 'new',
        })
        .then(res => {
          if (res.status === 200 && res.data) {
            // On success fetch the comments again for updating timestamp
            dispatch(MIC.GET_DISCUSSIONS, {
              issueId: params.issueId,
              discussionTypeId: params.discussionType,
            })
          }
        })
        .catch(error => {
          return Promise.reject(error)
        })
        .finally(() => {
          commit(MIC.UPDATE_MXREC_DISCUSSIONS_LOADING, {
            discussionTypeId: params.discussionType,
            loading: false,
          })
        })
    }
  },

  async [MIC.UPDATE_DISCUSSIONS]({ dispatch, commit }, params) {
    const currentIssue = params.issueId
    const mxRecLink = params.link

    // DetailsDiscussions
    commit(MIC.UPDATE_MXREC_DISCUSSIONS_LOADING, {
      discussionTypeId: state.detailsDiscussionTypeID,
      loading: true,
    })
    const detailsUpdates = Promise.all(
      state.mxRec.detailsDiscussion.map(async comment => {
        // New comment created
        if (comment.isNew) {
          await services.discussion
            .saveData('updateComment', {
              comment: {
                comment: comment.comment,
                discussionTypeId: state.detailsDiscussionTypeID,
                taggedUsers: comment.usersTagged ? comment.usersTagged : [],
                link: mxRecLink,
                issueId: currentIssue,
              },
              action: 'new',
            })
            .then(res => {
              if (res instanceof Error) return Promise.reject(res)
              // after the comment gets saved, find that comment and re-dispatch the action
              // with the database date so the added date shows immediately
              // when we add it won't have the database ID, so need to look through all the comments
              // eslint-disable-next-line
              state.mxRec.detailsDiscussion.map(commentObj => {
                if (commentObj.id === comment.id) {
                  dispatch(MIC.UPDATE_DISCUSSION_COMMENT, {
                    discussionType: state.detailsDiscussionTypeID,
                    comment: {
                      ...commentObj,
                      // for new comments, we need to update the comments
                      // with the new database id
                      newDbId: res.data.id,
                      isNew: undefined,
                      isUpdated: undefined,
                      createdOn: res.data.createdOn,
                    },
                  })
                }
              })
            })
        }
        // Update existing comment
        else if (comment.isUpdated) {
          await services.discussion
            .saveData('updateComment', {
              comment: {
                id: comment.id,
                comment: comment.comment,
                taggedUsers: comment.usersTagged ? comment.usersTagged : [],
                link: mxRecLink,
                discussionTypeId: state.detailsDiscussionTypeID,
                issueId: currentIssue,
              },
              action: 'update',
            })
            .then(res => {
              if (res instanceof Error) return Promise.reject(res)
              // after the comment gets saved, re-dispatch the action
              if (res.status === 200 && res.data === 1) {
                // On success fetch the comments again for updating timestamp
                dispatch(MIC.GET_DISCUSSIONS, {
                  issueId: currentIssue,
                  discussionTypeId: state.detailsDiscussionTypeID,
                })
              }
            })
        }
        // If a comment is deleted, only make the call to delete if it isn't a new comment
        else if (comment.isDeleted) {
          await services.discussion
            .saveData('deleteComment', {
              discussionId: comment.id,
            })
            .then(resp => {
              if (resp instanceof Error) return Promise.reject(resp)
              // upon successful deletion, remove it from the discussion array
              const updatedDetailsDiscusssion =
                state.mxRec.detailsDiscussion.filter(commentObj => {
                  return !commentObj.isDeleted
                })
              commit(MIC.UPDATE_DETAILS_DISCUSSION, updatedDetailsDiscusssion)
            })
        }
      }),
    )
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        commit(MIC.UPDATE_MXREC_DISCUSSIONS_LOADING, {
          discussionTypeId: state.detailsDiscussionTypeID,
          loading: false,
        })
      })

    // investigationDiscussion
    commit(MIC.UPDATE_MXREC_DISCUSSIONS_LOADING, {
      discussionTypeId: state.investigationDiscussionTypeID,
      loading: true,
    })

    const investigationUpdates = Promise.all(
      state.mxRec.investigationDiscussion.map(async comment => {
        // New comment created
        if (comment.isNew) {
          await services.discussion
            .saveData('updateComment', {
              comment: {
                comment: comment.comment,
                discussionTypeId: state.investigationDiscussionTypeID,
                taggedUsers: comment.usersTagged ? comment.usersTagged : [],
                link: mxRecLink,
                issueId: currentIssue,
              },
              action: 'new',
            })
            .then(res => {
              if (res instanceof Error) return Promise.reject(res)
              // after the comment gets saved, find that comment and re-dispatch the action
              // with the database date so the added date shows immediately
              // when we add it won't have the database ID, so need to look through all the comments
              // eslint-disable-next-line
              state.mxRec.investigationDiscussion.map(commentObj => {
                if (commentObj.id === comment.id) {
                  dispatch(MIC.UPDATE_DISCUSSION_COMMENT, {
                    discussionType: state.investigationDiscussionTypeID,
                    comment: {
                      // for new comments, we need to update the comments
                      // with the new database id
                      ...commentObj,
                      newDbId: res.data.id,
                      isNew: undefined,
                      isUpdated: undefined,
                      createdOn: !commentObj.createdOn
                        ? res.data.createdOn
                        : commentObj.createdOn,
                    },
                  })
                }
              })
            })
        }
        // Update existing comment
        else if (comment.isUpdated) {
          await services.discussion
            .saveData('updateComment', {
              comment: {
                id: comment.id,
                comment: comment.comment,
                discussionTypeId: state.investigationDiscussionTypeID,
                taggedUsers: comment.usersTagged ? comment.usersTagged : [],
                link: mxRecLink,
                issueId: currentIssue,
              },
              action: 'update',
            })
            .then(res => {
              if (res instanceof Error) return Promise.reject(res)
              // after the comment gets saved, re-dispatch the action
              // with the database date so the updated date shows immediately
              if (res.status === 200 && res.data === 1) {
                // On success fetch the comments again for updating timestamp
                dispatch(MIC.GET_DISCUSSIONS, {
                  issueId: currentIssue,
                  discussionTypeId: state.investigationDiscussionTypeID,
                })
              }
            })
        }
        // Delete existing comment
        else if (comment.isDeleted) {
          await services.discussion
            .saveData('deleteComment', {
              discussionId: comment.id,
            })
            .then(resp => {
              if (resp instanceof Error) return Promise.reject(resp)
              const updatedInvestigationDiscussion =
                state.mxRec.investigationDiscussion.filter(commentObj => {
                  return !commentObj.isDeleted
                })
              commit(
                MIC.UPDATE_INVESTIGATION_DISCUSSION,
                updatedInvestigationDiscussion,
              )
            })
        }
      }),
    )
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        commit(MIC.UPDATE_MXREC_DISCUSSIONS_LOADING, {
          discussionTypeId: state.investigationDiscussionTypeID,
          loading: false,
        })
      })

    const calls = await Promise.all(
      [detailsUpdates, investigationUpdates].map(p => p?.catch(error => error)),
    )
    const hasErrors = calls.filter(res => res instanceof Error)
    if (hasErrors.length) return Promise.reject(hasErrors[0])
  },

  async [MIC.GET_REMOVALS_DATA]({ commit }, removalsParams) {
    try {
      const removalsData = (
        await services.removals.getData('removals', { ...removalsParams }, true)
      ).data

      const updatedRemovalsData = removalsData?.map((removalData, index) => {
        return {
          rowId: index,
          ...removalData,
        }
      })

      commit(MIC.UPDATE_REMOVALS_DATA, {
        sectionTitle: 'Removals',
        grids: [
          {
            gridTitle: null,
            rowData: updatedRemovalsData,
            columnDefs: removalsColumnDefs,
            selectedRows: [],
          },
        ],
      })
    } catch (error) {
      console.log(`${[MIC.GET_REMOVALS_DATA]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'removals')
    }
  },

  async [MIC.GET_REMOVALS_DATA_BY_TAIL]({ commit }, removalsParams) {
    try {
      const removalsDataByTail = (
        await services.removals.getData(
          'removalsByTail',
          { ...removalsParams },
          true,
        )
      ).data

      const sortedData = removalsDataByTail
        .sort(
          (x, y) =>
            new Date(x.removalDate + 'Z').getTime() -
            new Date(y.removalDate + 'Z').getTime(),
        )
        .map(item => {
          return {
            ...item,
            name: item.partNumber,
            x: new Date(item.removalDate + 'Z').getTime(),
            y: 0,
          }
        })

      commit(MIC.UPDATE_REMOVALS_DATA_BY_TAIL, sortedData)
    } catch (error) {
      console.log(`${[MIC.GET_REMOVALS_DATA_BY_TAIL]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'removals')
    }
  },

  async [MIC.GET_FAULT_MESSAGES_DATA]({ commit }, faultMessagesParams) {
    try {
      const faultMessages = (
        await services.faultMessages.getData(
          'faultMessages',
          {
            ...faultMessagesParams,
            id: JSON.stringify({ ...faultMessagesParams }),
          },
          false,
          TIME_TO_LIVE_MINUTE * 10,
        )
      ).data
      // need to massage the data here to get unique identifiers
      // the unique identifiers for fault messages are concatenating file log ID, code and timestamp
      const updatedFaultMessages = faultMessages.codesByDate.map(
        faultMessage => {
          let formattedCode = faultMessage.code.toString()
          if (formattedCode.length === 7) {
            formattedCode = `${formattedCode.substr(
              0,
              2,
            )}-${formattedCode.substr(2)}`
          } else if (formattedCode.length === 8) {
            formattedCode = `${formattedCode.substr(
              0,
              3,
            )} ${formattedCode.substr(3, 3)} ${formattedCode.substr(5)}`
          }
          // we are making a unique identifier by concatenating the file log ID, code and timestamp (strip out the dashes and colons)
          const identifier = `${faultMessage.fileLogId.toString()}${faultMessage.code.toString()}${faultMessage.timeAlerted
            .replace(/-/g, '')
            .replace(/:/g, '')}`

          return {
            rowId: identifier,
            faultMessageType: faultMessage.messageType,
            faultMessageCode: formattedCode,
            faultMessageDate: faultMessage.timeAlerted,
          }
        },
      )

      // there still may be duplicates even from those. Need to filter out the duplicates.
      const uniqueFaultMessages = updatedFaultMessages.filter(function (
        obj,
        index,
        self,
      ) {
        return (
          index ===
          self.findIndex(t => {
            return t.rowId === obj.rowId
          })
        )
      })

      commit(MIC.UPDATE_FAULT_MESSAGES_DATA, {
        sectionTitle: 'Fault Messages',
        grids: [
          {
            gridTitle: null,
            rowData: uniqueFaultMessages,
            columnDefs: faultMessagesColumnDefs,
            selectedRows: [],
          },
        ],
      })
    } catch (error) {
      console.log(`${[MIC.GET_FAULT_MESSAGES_DATA]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'fault message')
    }
  },

  async [MIC.GET_PHM_ALERTS]({ commit }, phmAlertParams) {
    try {
      const phmAlerts = (
        await services.phmAlerts.getData('phmAlerts', phmAlertParams, true)
      ).data

      const updatedphmAlerts = phmAlerts.map(phmAlert => {
        return {
          rowId: phmAlert.id,
          ...phmAlert,
        }
      })

      commit(MIC.UPDATE_PHM_ALERTS, {
        sectionTitle: 'PHM Alerts',
        grids: [
          {
            gridTitle: null,
            rowData: updatedphmAlerts,
            columnDefs: phmAlertColumnDefs,
            selectedRows: [],
          },
        ],
      })
    } catch (error) {
      console.log(`${[MIC.GET_PHM_ALERTS]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'PHM alert')
    }
  },

  [MIC.UPDATE_SUPPORTING_INFO_CAPTION]({ commit }, captionParams) {
    commit(MIC.SET_CAPTION, captionParams)
  },

  async [MIC.GET_CUSTOM_ALERT_MAINTENANCE_ISSUE_VERSION]({ commit }, issueId) {
    commit(MIC.UPDATE_CUSTOM_ALERT_MAINTENANCE_ISSUE_VERSION, {})
    const response = await services.maintenanceIssues.getData(
      'customAlertMaintenanceIssueVersion',
      issueId,
      true,
    )
    let data = response.data !== '' ? response.data : {}
    commit(MIC.UPDATE_CUSTOM_ALERT_MAINTENANCE_ISSUE_VERSION, data)
  },
  async [MIC.RESET_CUSTOM_ALERT_MAINTENANCE_ISSUE_VERSION]({ commit }) {
    commit(MIC.UPDATE_CUSTOM_ALERT_MAINTENANCE_ISSUE_VERSION, {})
  },

  async [MIC.GET_MAINTENANCE_RECS]({ commit }) {
    commit(MIC.UPDATE_MAINTENANCE_RECS, [])
    try {
      const response = await services.maintenanceIssues.getData(
        'maintenanceIssues',
        null,
        true,
      )

      const data = response.data.map(item => {
        return {
          airlineId: item.airline.id,
          airlineName: item.airline.name,
          masterModelId: item.masterModel.id,
          masterModelName: item.masterModel.name,
          systemId: item.system ? item.system.id : null,
          systemName: item.system ? item.system.name : null,
          componentId: item.component ? item.component.id : null,
          componentName: item.component ? item.component.name : null,
          statusId: item.status.id,
          statusName: item.status.name,
          externalStatus: item.status.isExternal,
          analyticId: item.analytic ? item.analytic.id : '',
          analyticName: item.analytic ? item.analytic.name : '',
          analyticModule: item.analyticModuleName
            ? item.analyticModuleName
            : null,
          analyticAlert: item.analyticAlert,
          customAlertId: item.customAlertId,
          customAlertName: item.customAlertName,
          tailId: item.tail.id,
          tailNumber: item.tail.name,
          alertCount: item.alertCount,
          id: item.id,
          issueId: item.issueId || item.id,
          partNumber: item.partNumber,
          positionName: item.positionName,
          ammtask: item.ammtask,
          risk: item.risk,
          alertType: item.source.name,
          createdDate: item.createdDate,
          lastAlertDate: item.lastAlertDate,
          diffDays: item.dateSubmitted
            ? moment().diff(moment.utc(item.dateSubmitted), 'd')
            : null,
        }
      })
      commit(MIC.UPDATE_MAINTENANCE_RECS, data)
    } catch (error) {
      console.log(`${[MIC.GET_MAINTENANCE_RECS]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'maintenance Recs')
    }
  },

  async [MIC.GET_MAINTENANCE_GRID]({ commit, rootGetters }, isRefresh) {
    try {
      const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
      const platforms = rootGetters['globalFilter/masterModelIdsSelected']
      const filters = {
        airlineIds: airlineIds,
        platforms: platforms,
        id: JSON.stringify({ airlineIds: airlineIds, platforms: platforms }),
      }
      const response = await services.maintenanceIssues.getData(
        'maintenanceGrid',
        filters,
        isRefresh,
        TIME_TO_LIVE_MINUTE * 5,
      )
      const data = response.data.map(item => {
        return {
          airlineId: item.airline.id,
          airlineName: item.airline.name,
          masterModelId: item.masterModel.id,
          masterModelName: item.masterModel.name,
          // systemName: item.system ? item.system.name : null,
          // componentName: item.component ? item.component.name : null,
          positionName: item.positionName,
          statusId: item.status.id,
          statusName: item.status.name,
          analyticModule: item.analyticModuleName
            ? item.analyticModuleName
            : null,
          customAlertName: item.customAlertName,
          tailNumber: item.tail.name,
          alertCount: item.alertCount,
          id: item.id,
          issueId: item.issueId || item.id,
          alertType: item.source.name,
          lastAlertDate: item.lastAlertDate,
          createdDate: item.createdDate,
          uniqueChronicId: item.uniqueChronicId || null,
          fmtOwnerName: item.fmtOwnerName,
          ataChapter: item.ataChapter,
          sourceId: item.source.id,
        }
      })
      commit(MIC.UPDATE_MAINTENANCE_GRID, data)
    } catch (error) {
      console.log(`${[MIC.GET_MAINTENANCE_GRID]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'maintenance grid')
    }
  },

  [MIC.UPDATE_MXREC_ALERT_DETAILS]({ commit }, detailObj) {
    commit(MIC.UPDATE_ACTIVE_MXREC, {
      severityId: detailObj?.severityId,
    })
  },

  [MIC.UPDATE_MXREC_STATUS_DETAILS]({ commit }, detailObj) {
    commit(MIC.UPDATE_ACTIVE_MXREC, {
      assetSupportPOC: detailObj?.assetSupportPOC,
      mxRecSubmitterId: detailObj?.personWhoSentMxRec,
      ownerUserId: detailObj?.ownerUserId,
      status: detailObj?.status,
    })
  },

  [MIC.UPDATE_MXREC_RECOMMENDATION]({ commit }, recommendation) {
    commit(MIC.UPDATE_ACTIVE_MXREC, {
      recommendation: recommendation,
    })
  },

  [MIC.UPDATE_MXREC_VALIDATION_DETAILS]({ commit }, detailObj) {
    commit(MIC.UPDATE_ACTIVE_MXREC, {
      fixEffectivenessId: detailObj.fixEffectivenessId,
      serviceDeliveryOutcomeId: detailObj.serviceDeliveryOutcomeId,
      analyticDetectionOutcomeId: detailObj.analyticDetectionOutcomeId,
      expectedServiceInterruptionMinutesSaved:
        detailObj.expectedServiceInterruptionMinutesSaved,
    })
  },

  [MIC.UPDATE_MXREC_CONCLUDE_DETAILS]({ commit }, detailObj) {
    commit(MIC.UPDATE_ACTIVE_MXREC, {
      mroLocation: detailObj.mroLocation,
      mroPoc: detailObj.mroPOC,
      closingReasonId: detailObj.reasonToCloseId,
      dateOfRemoval: detailObj.removalDate,
    })
  },

  [MIC.UPDATE_MXREC_FOLLOWUP_FIELDS]({ commit }, detailObj) {
    commit(MIC.UPDATE_ACTIVE_MXREC, {
      airlineResponseId: detailObj.airlineResponseId,
      installedPartNumber: detailObj.installedPartNumber,
      installedPartSerialNumber: detailObj.installedPartSerialNumber,
      serviceAlertNumber: detailObj.serviceAlertNumber,
      purchaseOrderNumber: detailObj.purchaseOrderNumber,
      dateCompleted: detailObj.dateCompleted,
      removedPartNumber: detailObj.removedPartNumber,
      removedPartSerialNumber: detailObj.removedPartSerialNumber,
    })
  },

  [MIC.UPDATE_MXREC_TITLE]({ commit }, title) {
    commit(MIC.UPDATE_ACTIVE_MXREC, {
      title: title,
    })
  },

  async [MIC.GET_TAGGABLE_USERS]({ commit }, issueId) {
    try {
      await services.discussion
        .getData('taggableUsers', issueId, true)
        .then(res => {
          commit(MIC.UPDATE_DETAIL_USERS, res.data.detailsUsers)
          commit(MIC.UPDATE_INVESTIGATOR_USERS, res.data.investigationUsers)
        })
    } catch (error) {
      console.log(`${[MIC.GET_TAGGABLE_USERS]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'tagging user')
    }
  },

  async [MIC.GET_SEVERITY_OPTIONS]({ commit }) {
    try {
      await services.maintenanceIssues.getData('severityOptions').then(res => {
        const mappedOptions = res.data.map(obj => ({
          text: obj.name,
          value: obj.id,
        }))
        commit(MIC.UPDATE_SEVERITY_OPTIONS, mappedOptions)
      })
    } catch (error) {
      console.log(`${[MIC.GET_SEVERITY_OPTIONS]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'severity options')
    }
  },

  async [MIC.GET_FIX_EFFECTIVENESS_OPTIONS]({ commit }) {
    try {
      await services.maintenanceIssues
        .getData('fixEffectivenessOptions')
        .then(res => {
          const mappedOptions = res.data.map(obj => ({
            text: obj.name,
            value: obj.id,
          }))
          commit(MIC.UPDATE_FIX_EFFECTIVENESS_OPTIONS, mappedOptions)
        })
    } catch (error) {
      console.log(`${[MIC.GET_FIX_EFFECTIVENESS_OPTIONS]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'fix effectiveness options')
    }
  },

  async [MIC.GET_SERVICE_DELIVERY_OUTCOME_OPTIONS]({ commit }) {
    try {
      await services.maintenanceIssues
        .getData('serviceDeliveryOutcomeOptions')
        .then(res => {
          const mappedOptions = res.data.map(obj => ({
            text: obj.name,
            value: obj.id,
          }))
          commit(MIC.UPDATE_SERVICE_DELIVERY_OUTCOME_OPTIONS, mappedOptions)
        })
    } catch (error) {
      console.log(
        `${[MIC.GET_SERVICE_DELIVERY_OUTCOME_OPTIONS]}: error ${error}`,
      )
      commit(MIC.UPDATE_MX_ERRORS, 'service delivery options')
    }
  },

  async [MIC.GET_ANALYTIC_DETECTION_OUTCOME_OPTIONS]({ commit }) {
    try {
      await services.maintenanceIssues
        .getData('analyticDetectionOptions')
        .then(res => {
          const mappedOptions = res.data.map(obj => ({
            text: obj.name,
            value: obj.id,
          }))
          commit(MIC.UPDATE_ANALYTIC_DETECTION_OUTCOME_OPTIONS, mappedOptions)
        })
    } catch (error) {
      console.log(
        `${[MIC.GET_ANALYTIC_DETECTION_OUTCOME_OPTIONS]}: error ${error}`,
      )
      commit(MIC.UPDATE_MX_ERRORS, 'analytic detection options')
    }
  },

  async [MIC.GET_REASON_TO_CLOSE_OPTIONS]({ commit }) {
    try {
      await services.maintenanceIssues
        .getData('reasonToCloseOptions')
        .then(res => {
          const mappedOptions = res.data.map(obj => ({
            text: obj.name,
            value: obj.id,
          }))
          commit(MIC.UPDATE_REASON_TO_CLOSE_OPTIONS, mappedOptions)
        })
    } catch (error) {
      console.log(`${[MIC.GET_REASON_TO_CLOSE_OPTIONS]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'closing reason options')
    }
  },

  async [MIC.GET_AIRLINE_RESPONSE_OPTIONS]({ commit }) {
    try {
      await services.maintenanceIssues
        .getData('airlineResponseOptions')
        .then(res => {
          const mappedOptions = res.data.map(obj => ({
            text: obj.name,
            value: obj.id,
          }))
          commit(MIC.UPDATE_AIRLINE_RESPONSE_OPTIONS, mappedOptions)
        })
    } catch (error) {
      console.log(`${[MIC.GET_AIRLINE_RESPONSE_OPTIONS]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'airline response options')
    }
  },

  // eslint-disable-next-line no-unused-vars
  async [MIC.UPDATE_PLOT_TO_ISSUE_ID_LINK]({ dispatch }, issuePlotMapping) {
    await services.maintenanceIssues.saveData(
      'maintenanceIssuePlots',
      issuePlotMapping,
    )
  },

  async [MIC.GET_SUPPORTING_INFO_SELECTIONS]({ commit, state }, issueId) {
    try {
      // get the types and map those out...then get the selections for each type
      const whichIssue = issueId
      const supportingInfoTypes = (
        await services.lookup.getData('supportingInfoTypes')
      ).data
      // need to map these to id and name, since it comes back from the API
      let mappedSupportingInfoTypes = Object.keys(supportingInfoTypes).map(
        key => {
          return {
            id: Number(key),
            name: supportingInfoTypes[key],
          }
        },
      )

      // update the store with the types and IDs
      commit(
        MIC.UPDATE_SUPPORTING_INFO_SELECTION_TYPES,
        mappedSupportingInfoTypes,
      )
      let supportingInfoSelections = await services.selections.getData(
        'supportingInfoVisibilitySelections',
        {
          issueId: whichIssue,
        },
        true,
      )

      mappedSupportingInfoTypes.map(async supportingInfoType => {
        switch (supportingInfoType.name) {
          case 'ATTACHMENTS':
            break
          case 'PHM_ALERTS': {
            const phmAlerts = state.mxRec?.phmAlerts?.grids[0].rowData
            supportingInfoSelections = {
              ...supportingInfoSelections,
              selections: supportingInfoSelections?.selections?.filter(
                selection =>
                  typeof selection === 'number' &&
                  !!phmAlerts.find(alert => alert.rowId === selection),
              ),
            }
            break
          }
        }

        if (supportingInfoSelections) {
          commit(MIC.UPDATE_SUPPORTING_INFO_SELECTIONS, {
            name: supportingInfoType.name,
            selections: supportingInfoSelections,
            caption: supportingInfoSelections?.data
              ? supportingInfoSelections.data.find(
                  x => x.typeId === supportingInfoType.id,
                )?.caption
              : '',
          })
        }
      })
    } catch (error) {
      console.log(`${[MIC.GET_SUPPORTING_INFO_SELECTIONS]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'some supporting information')
    }
  },

  [MIC.UPDATE_SUPPORTING_INFO_ROW_SELECTION]({ commit }, rowSelectionParams) {
    commit(MIC.UPDATE_ROW_VISIBILITY, rowSelectionParams)
  },

  async [MIC.SAVE_SUPPORTING_INFO_SELECTIONS]({ commit, state }, issueId) {
    // iterate over the selection types and make a call to save each one
    await Promise.all(
      state.supportingInfoSelectionTypes.map(async typeObj => {
        let params = {
          issueId: issueId,
        }
        switch (typeObj.name) {
          case 'FAULT_MESSAGES':
            params = {
              ...params,
              typeId: typeObj.id,
              selections: state.mxRec.faultMessages.selectedRows,
              caption: state.mxRec.faultMessages.caption,
            }
            break
          case 'ATTACHMENTS':
            params = {
              ...params,
              typeId: typeObj.id,
              // These are selected plot attachments...not file attachments!
              // those are handled in the attachments store
              selections: state.mxRec.visiblePlots,
              // we are saving the two captions as a stringified object
              caption: JSON.stringify({
                attachmentsCaption: state.mxRec.attachmentsCaption,
                plotsCaption: state.mxRec.plotsCaption,
              }),
            }
            break
          case 'PHM_ALERTS':
            params = {
              ...params,
              typeId: typeObj.id,
              selections: state.mxRec.phmAlerts.selectedRows,
              caption: state.mxRec.phmAlerts.caption,
            }
            break
          case 'REMOVALS':
            // params = {
            //   ...params,
            //   typeId: typeObj.id,
            //   content: state.mxRec.removals.selectedRows,
            //   caption: state.mxRec.removals.caption,
            // }
            break
        }
        // don't make an API call for removals, since we're not currently
        // saving selections for them
        if (typeObj.name !== 'REMOVALS') {
          await services.selections
            .saveData('userSelections', params)
            .then(resp => {
              if (resp instanceof Error) return Promise.reject(resp)
            })
        }
      }),
      commit(MIC.UPDATE_PRESERVED_MXREC, state.mxRec),
    ).catch(error => {
      return Promise.reject(error)
    })
  },

  async [MIC.REMOVE_DELETED_PLOT_FROM_SELECTIONS]({ state }, params) {
    const updatedPlotSelections = state.mxRec?.visiblePlots?.filter(
      selectedPlotId => {
        return selectedPlotId !== params.plotId
      },
    )
    const selectionParams = {
      issueId: params.issueId,
      typeId: params.type.id,
      selections: updatedPlotSelections,
      caption: JSON.stringify({
        attachmentsCaption: state.mxRec.attachmentsCaption,
        plotsCaption: state.mxRec.plotsCaption,
      }),
    }
    await services.selections.saveData('userSelections', selectionParams)
  },

  async [MIC.GET_SESSION_EXPIRATION]({ commit }, sessionFlag) {
    commit(MIC.UPDATE_SESSION_EXPIRATION, sessionFlag)
  },

  async [MIC.GET_MAINTENANCE_ISSUE_PLOTS]({ commit }, issueId) {
    try {
      // Get the plots related to maintenance here which should be of plotTypeId 2
      const issuePlots = (
        await services.plot.getData('plotByIssueId', issueId, true)
      ).data

      const parsedPlots = issuePlots
        .filter(pagePlot => {
          let isValidJson = true
          try {
            JSON.parse(pagePlot.json)
          } catch (err) {
            isValidJson = false
          }
          return isValidJson
        })
        .map(pagePlot => {
          const parsed = JSON.parse(pagePlot.json)
          return {
            ...parsed,
            createdId: pagePlot.createdId || pagePlot.basePlotId,
            id: pagePlot.basePlotId,
            maintenanceIssueIds: pagePlot.maintenanceIssueIds,
            serverId: pagePlot.basePlotId,
          }
        })

      commit(MIC.SET_MAINTENANCE_ISSUE_PLOTS, parsedPlots)
    } catch (error) {
      console.log(`${[MIC.GET_MAINTENANCE_ISSUE_PLOTS]}: error ${error}`)
      commit(MIC.UPDATE_MX_ERRORS, 'plot')
    }
  },

  [MIC.UPDATE_PLOT_VISIBILITY]({ commit }, plotParams) {
    commit(MIC.SET_PLOT_VISIBILITY, plotParams)
  },

  // eslint-disable-next-line no-unused-vars
  async [MIC.DELETE_PLOT_LINK]({ commit, state }, plotToDeleteParams) {
    const issuePlotMapping = {
      plotId: plotToDeleteParams.plotId,
      issueId: plotToDeleteParams.issueId,
    }
    await services.maintenanceIssues.deleteData(
      'maintenanceIssuePlot',
      issuePlotMapping,
    )
  },

  // eslint-disable-next-line no-unused-vars
  async [MIC.GET_MAINTENANCE_ISSUE_LOCK_STATUS]({ commit }, lockOptions) {
    try {
      const response = await services.maintenanceIssues.getData(
        'maintenanceIssueLockStatus',
        lockOptions,
        true,
      )
      return response.data
    } catch (error) {
      console.log(`${[MIC.GET_MAINTENANCE_ISSUE_LOCK_STATUS]}: error ${error}`)
    }
  },

  async [MIC.SAVE_MAINTENANCE_ISSUE_LOCK_STATUS]({ dispatch }, lockOptions) {
    try {
      await services.maintenanceIssues.saveData(
        'maintenanceIssueLockStatus',
        lockOptions,
      )
      const response = await dispatch(MIC.GET_MAINTENANCE_ISSUE_LOCK_STATUS, {
        issueId: lockOptions.issueId,
      })
      return response
    } catch (error) {
      console.log(`${[MIC.SAVE_MAINTENANCE_ISSUE_LOCK_STATUS]}: error ${error}`)
    }
  },

  [MIC.UPDATE_ATTACHMENT_VISIBILITY]({ commit }, attachmentInfo) {
    commit(MIC.SET_ATTACHMENT_VISIBILITY, attachmentInfo)
  },

  [MIC.RESET_MX_ERRORS]({ commit }) {
    commit(MIC.UPDATE_MX_ERRORS)
  },

  async [MIC.GET_HOME_PAGE_MAINTENANCE_ISSUES]({ commit, rootGetters }) {
    const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
    const masterModelIds = rootGetters['globalFilter/masterModelIdsSelected']
    const filters = {
      airlineIds: airlineIds,
      platforms: masterModelIds,
    }
    try {
      const response = await services.maintenanceIssues.getData(
        'homePageMaintenanceIssues',
        filters,
        true,
      )
      if (response) {
        const data = response.data.map(item => {
          return {
            ...item,
            airlineId: item.airline.id,
            airlineName:
              item.airline.name === null || item.airline.name === ''
                ? 'N/A'
                : item.airline.name,
            masterModelId: item.masterModel.id,
            masterModelName:
              item.masterModel.name === null || item.masterModel.name === ''
                ? 'N/A'
                : item.masterModel.name,
            systemId: item.system?.id,
            systemName:
              item.system === null
                ? '-'
                : item.system.name === null || item.system.name === ''
                ? 'N/A'
                : item.system.name,
            componentId: item.component?.id,
            componentName:
              item.component === null
                ? '-'
                : item.component?.name
                ? item.positionName
                  ? `${item.component.name} (${item.positionName})`
                  : item.component.name
                : 'N/A',
            statusId: item.status.id,
            statusName: item.status.name,
            externalStatus: item.status.isExternal,
            analyticId: item.analytic ? item.analytic.id : '',
            analyticName:
              item.analytic?.name &&
              !(item.analytic.name === null || item.analytic.name === '')
                ? item.analytic.name
                : 'N/A',
            analyticModule: item.analyticModuleName
              ? item.analyticModuleName
              : 'N/A',
            analyticAlert: item.analyticAlert,
            tailId: item.tail.id,
            tailNumber: item.tail.name,
            alertCount: item.alertCount,
            id: item.id,
            issueId: item.issueId || item.id,
            partNumber: item.partNumber,
            ammtask: item.ammtask,
            risk: item.risk,
            alertType: item.source.name,
            createdDate: item.createdDate,
            lastAlertDate: item.lastAlertDate,
            lastAlertDateInt: new Date(item.lastAlertDate).getTime(),
            diffDays: item.dateSubmitted
              ? moment().diff(moment.utc(item.dateSubmitted), 'd')
              : null,
          }
        })
        commit(MIC.UPDATE_HOME_PAGE_MAINTENANCE_ISSUES, data)
      }
    } catch (error) {
      commit(MIC.UPDATE_HOME_PAGE_MAINTENANCE_ISSUES, [])
    }
  },

  [MIC.CANCEL_CHANGES]({ commit }) {
    const previousState = JSON.parse(JSON.stringify(state.preservedMxRec))
    commit(MIC.UPDATE_ACTIVE_MXREC, previousState)
  },

  async [MIC.GET_MXREC_ALERTS_EXPORT_GIVEN_OPTIONS](
    { commit, rootGetters },
    options,
  ) {
    try {
      commit(MIC.UPDATE_MXREC_ALERTS_EXPORT_LOADING, true)
      const airlines = rootGetters[`${UC.NAMESPACE}/mappedUserAirlines`]
      const airlineIds = rootGetters['globalFilter/airlineIdsSelected']
      const masterModelIds = rootGetters['globalFilter/masterModelIdsSelected']
      const airlineICAO = airlines
        .filter(item => airlineIds.includes(item.airlineId))
        .map(item => item.icaoCode)

      const params = {
        startDate: options.startDate,
        endDate: options.endDate,
        airlines: airlineICAO,
        masterModels: masterModelIds,
        dateFilterOn: options.dateFilterOn,
      }

      await services.maintenanceIssues.getData(
        'mxRecAlertsExportGivenOptions',
        params,
        true,
      )
    } catch (error) {
      console.log(MIC.GET_MXREC_ALERTS_EXPORT_GIVEN_OPTIONS + ': ' + error)
    } finally {
      commit(MIC.UPDATE_MXREC_ALERTS_EXPORT_LOADING, false)
    }
  },

  async [MIC.GET_ISSUE_SOURCE_OPTIONS]({ commit }) {
    try {
      await services.maintenanceIssues
        .getData('issueSourceOptions')
        .then(res => {
          const sourceOptions = res.data.map(source => ({
            isManual: source.isManual,
            text: source.displayName,
            value: source.id,
          }))
          commit(MIC.UPDATE_ISSUE_SOURCE_OPTIONS, sourceOptions)
        })
    } catch (error) {
      console.log(`${[MIC.GET_ISSUE_SOURCE_OPTIONS]}: error ${error}`)
      return Promise.reject(error)
    }
  },

  async [MIC.ADD_MANUAL_MAINTENANCE_ISSUE](_, manualMxRec) {
    try {
      const mxRecToAdd = {
        title: 'Title Update Needed',
        tail: manualMxRec.tailName,
        partNumber: manualMxRec.partNumber,
        component: manualMxRec.componentId,
        sourceId: manualMxRec.sourceId,
        maintenanceIssueStatusId: manualMxRec.status,
        analyticModuleId: manualMxRec.analyticModuleId,
        airlineAgreementTypeId: COLLINS_PHM_TIER_4,
      }

      const response = await services.maintenanceIssues.saveData(
        'maintenanceIssue',
        mxRecToAdd,
      )

      if (response.status === 201) {
        return parseInt(response.headers.location.split('/').pop())
      }
    } catch (error) {
      console.log(`${[MIC.ADD_MANUAL_MAINTENANCE_ISSUE]}: error ${error}`)
    }
  },

  async [MIC.CREATE_NEW_REPEATER_ISSUE](_, repeaterIssue) {
    try {
      const response = await services.maintenanceIssues.saveData(
        'repeaterIssue',
        repeaterIssue,
      )

      if (response.status === 201) {
        return parseInt(response.headers.location.split('/').pop())
      }
    } catch (error) {
      console.log(`${[MIC.CREATE_NEW_REPEATER_ISSUE]}: error ${error}`)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
